import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import ManagerLayout from "@/components/ManagerLayout";
import { Calendar, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatMatchDate, parseMatchDate } from "@/lib/dates";

const ManagerMatches = () => {
  const [matches, setMatches] = useState([]);
  const [team, setTeam] = useState(null);
  const [teamsMap, setTeamsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [resultData, setResultData] = useState({ home_score: 0, away_score: 0 });

  const fetchData = async () => {
    try {
      const [teamRes, matchesRes, allTeamsRes] = await Promise.all([
        axios.get(`${API}/manager/my-team`, { withCredentials: true }),
        axios.get(`${API}/manager/my-matches`, { withCredentials: true }),
        axios.get(`${API}/teams`)
      ]);
      setTeam(teamRes.data);
      setMatches(matchesRes.data);
      const tMap = {};
      allTeamsRes.data.forEach(t => { tMap[t.team_id] = t; });
      setTeamsMap(tMap);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleResultSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMatch) return;

    try {
      await axios.put(
        `${API}/matches/${selectedMatch.match_id}/result`,
        {
          home_score: parseInt(resultData.home_score),
          away_score: parseInt(resultData.away_score),
          status: "completed"
        },
        { withCredentials: true }
      );
      toast.success("Match result saved - standings updated!");
      setIsResultDialogOpen(false);
      setSelectedMatch(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save result");
    }
  };

  const openResultDialog = (match) => {
    setSelectedMatch(match);
    setResultData({
      home_score: match.home_score || 0,
      away_score: match.away_score || 0
    });
    setIsResultDialogOpen(true);
  };

  const formatDate = (d) => formatMatchDate(d, { weekday: 'short', month: 'short', day: 'numeric' });

  const fixtures = matches.filter(m => m.status === "scheduled").sort((a,b) => parseMatchDate(a.match_date) - parseMatchDate(b.match_date));
  const results = matches.filter(m => m.status === "completed").sort((a,b) => parseMatchDate(b.match_date) - parseMatchDate(a.match_date));

  const MatchCard = ({ match, isResult = false }) => {
    const isHome = match.home_team_id === team?.team_id;
    const opp = teamsMap[isHome ? match.away_team_id : match.home_team_id];
    const teamScore = isHome ? match.home_score : match.away_score;
    const oppScore = isHome ? match.away_score : match.home_score;
    const result = isResult ? (teamScore > oppScore ? 'W' : teamScore < oppScore ? 'L' : 'D') : null;

    return (
      <div className={`bg-white p-4 flex flex-col md:flex-row items-center gap-4 border-l-4 ${result === 'W' ? 'border-[#009E60]' : result === 'L' ? 'border-red-500' : result === 'D' ? 'border-gray-400' : 'border-[#FFC72C]'}`} data-testid={`match-card-${match.match_id}`}>
        <div className="text-sm text-gray-500 w-full md:w-32">
          <p className="font-medium">{formatDate(match.match_date)}</p>
          <p className="text-xs">{match.match_time || 'TBD'}</p>
        </div>
        <div className="flex items-center gap-4 flex-1 justify-center">
          <span className={`px-2 py-1 text-xs font-bold uppercase ${isHome ? 'bg-[#009E60] text-white' : 'bg-gray-200'}`}>
            {isHome ? 'HOME' : 'AWAY'}
          </span>
          <span className="text-gray-400 text-sm">vs</span>
          <div className="flex items-center gap-2">
            <img 
              src={opp?.logo_url || 'https://placehold.co/40x40/09090b/FFF?text=T'} 
              alt="" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-heading font-bold">{opp?.name || 'Unknown'}</span>
          </div>
          {isResult && (
            <div className="font-heading font-bold text-2xl ml-4">
              {teamScore} - {oppScore}
            </div>
          )}
        </div>
        <div>
          {!isResult ? (
            <Button
              onClick={() => openResultDialog(match)}
              className="bg-[#009E60] hover:bg-[#008050] text-white rounded-none"
              data-testid={`enter-result-${match.match_id}`}
            >
              Enter Result
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className={`form-badge form-badge-${result.toLowerCase()}`}>{result}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openResultDialog(match)}
                className="rounded-none"
                data-testid={`edit-result-${match.match_id}`}
              >
                Edit
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <ManagerLayout title="Matches & Results">
      <div data-testid="manager-matches">
        {loading ? (
          <p className="text-gray-500">Loading matches...</p>
        ) : (
          <Tabs defaultValue="fixtures" className="w-full">
            <TabsList className="w-full justify-start border-b border-gray-200 mb-8 bg-transparent h-auto p-0">
              <TabsTrigger 
                value="fixtures"
                className="px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent"
                data-testid="tab-fixtures"
              >
                Upcoming ({fixtures.length})
              </TabsTrigger>
              <TabsTrigger 
                value="results"
                className="px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent"
                data-testid="tab-results"
              >
                Results ({results.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="fixtures">
              {fixtures.length > 0 ? (
                <div className="space-y-3">
                  {fixtures.map(m => <MatchCard key={m.match_id} match={m} />)}
                </div>
              ) : (
                <div className="text-center py-16 bg-white border border-gray-200">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No upcoming matches scheduled.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="results">
              {results.length > 0 ? (
                <div className="space-y-3">
                  {results.map(m => <MatchCard key={m.match_id} match={m} isResult />)}
                </div>
              ) : (
                <div className="text-center py-16 bg-white border border-gray-200">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No results yet.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Result Dialog */}
        <Dialog open={isResultDialogOpen} onOpenChange={setIsResultDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold uppercase">Enter Match Result</DialogTitle>
            </DialogHeader>
            {selectedMatch && (
              <form onSubmit={handleResultSubmit} className="space-y-6" data-testid="result-form">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-center flex-1">
                    <img 
                      src={teamsMap[selectedMatch.home_team_id]?.logo_url || 'https://placehold.co/60x60/09090b/FFF?text=H'} 
                      alt="" 
                      className="w-16 h-16 mx-auto mb-2 object-contain"
                    />
                    <p className="font-heading font-bold text-sm">{teamsMap[selectedMatch.home_team_id]?.short_name}</p>
                    <Input
                      type="number" min="0"
                      value={resultData.home_score}
                      onChange={(e) => setResultData({...resultData, home_score: e.target.value})}
                      className="w-20 mx-auto mt-2 text-center text-2xl font-bold rounded-none"
                      data-testid="home-score-input"
                    />
                  </div>
                  <span className="text-2xl font-bold text-gray-300">-</span>
                  <div className="text-center flex-1">
                    <img 
                      src={teamsMap[selectedMatch.away_team_id]?.logo_url || 'https://placehold.co/60x60/09090b/FFF?text=A'} 
                      alt="" 
                      className="w-16 h-16 mx-auto mb-2 object-contain"
                    />
                    <p className="font-heading font-bold text-sm">{teamsMap[selectedMatch.away_team_id]?.short_name}</p>
                    <Input
                      type="number" min="0"
                      value={resultData.away_score}
                      onChange={(e) => setResultData({...resultData, away_score: e.target.value})}
                      className="w-20 mx-auto mt-2 text-center text-2xl font-bold rounded-none"
                      data-testid="away-score-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#009E60] hover:bg-[#008050] text-white rounded-none" data-testid="save-result-btn">
                  <Check className="w-4 h-4 mr-2" /> Save Result & Update Standings
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ManagerLayout>
  );
};

export default ManagerMatches;
